import type { MetaFunction } from "@remix-run/react"
import {
  articleBodyStyle,
  articleSection,
  bold,
  h1Style,
  h1Wrap,
  h2Style,
  headerTextStyle,
  highlightHead,
  imagePreview2,
  orderedList,
  imageGridTitle,
  largeImageGrid,
} from "./article.css"
import Footer from "~/components/Footer"
import Link from "~/components/Link"
import { golden } from "~/components/styles/theme.css"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import StickyHeader from "~/components/StickyHeader"

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = { url: string; initial?: PreviewImage | null; loading?: boolean }

export const meta: MetaFunction = () => {
  return [
    {
      title: "The Future of Garden Design | AI Landscape Design | Rescape",
    },
    {
      name: "description",
      content:
        "Discover the future of garden design with Rescape's AI landscape design tool. Create innovative, nature-inspired outdoor spaces effortlessly.",
    },
    {
      property: "og:title",
      content: "Experience the Future of Landscaping with AI Garden Design",
    },
    {
      property: "og:description",
      content:
        "Discover the transformative power of AI garden design with Rescape AI. Create stunning, wildlife-friendly spaces with the click of a button.",
    },
    {
      property: "og:image",
      content:
        "https://rescapeai.com/transform-your-garden-with-rescape-ai/wildlife-friendly-natural-pond.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/article/transform-your-garden-with-rescape-ai",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "The Future of Garden Design | AI Landscape Design | Rescape",
        description:
          "Discover the future of garden design with Rescape's AI landscape design tool. Create innovative, nature-inspired outdoor spaces effortlessly.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2024-05-15",
        dateModified: "2024-05-15",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/ai-garden-design/cozy-balcony-ai-generated.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/article/transform-your-garden-with-rescape-ai",
        },
        articleSection: [
          "Technology",
          "Landscape Design",
          "Artificial Intelligence",
          "Gardening",
        ],
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/ai-garden-design/cozy-balcony-ai-generated.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Transform Your Garden with Rescape AI: The Future of Garden Design
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <section className={articleSection}>
          <p className={articleBodyStyle}>
            Redesigning your garden in a style that works for your space as well as
            matching your taste can become laborious and tiresome without the right
            direction. You could hire a professional landscaper or garden architect to
            help define a space that works for you. But these options are costly and time
            consuming.
          </p>
          <p className={articleBodyStyle}>
            Did you ever imagine how a wildlife pond would look in your backyard? A
            wildlife haven for the local critters and creatures? Maybe even a{" "}
            <Link href="/article/contemporary-garden-ideas-with-rescape-ai">
              Contemporary pergola
            </Link>{" "}
            or relaxing seating area?
          </p>
          <p className={articleBodyStyle}>
            Utilising Rescape AI for your garden design tasks quickly and powerfully
            generates realistic imagery of your garden. Experimenting with creativity
            gives the model more or less control over the output generated. A high
            creativity setting will allow the AI to take more artistic liberties,
            providing you with unique and innovative{" "}
            <Link href="/article/back-garden-ideas">garden design ideas</Link>, while a
            lower setting will stick closer to your original photograph, ensuring a more
            faithful representation.
          </p>
          <p className={articleBodyStyle}>
            The process is simple. Photograph a clear and focused image of your garden. It
            should be well lit so the garden design AI can do its work. Utilising
            generative AI for garden design tasks is easy with Rescape AI, and the
            intuitive form in the user interface makes generating images a breeze.
          </p>
        </section>
        <section className={articleSection}>
          <div className={largeImageGrid} style={{ marginTop: 0 }}>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>
                Wildlife friendly garden with a natural pond
              </h2>
              <img
                src="/transform-your-garden-with-rescape-ai/wildlife-friendly-natural-pond.webp"
                alt="Wildlife friendly garden, expertly designed and including a natural pond to invite wildlife into the garden."
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
            </div>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>Alternative style, same instructions</h2>
              <img
                src="/transform-your-garden-with-rescape-ai/wildlife-friendly-natural-pond-2.webp"
                alt="A natural pond, naturally integrated to the layout of the clients garden."
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
            </div>
          </div>
        </section>
        <section className={articleSection}>
          <h2 className={h2Style}>Why Choose Rescape AI for Your Garden Design?</h2>
          <ol className={orderedList}>
            <li>
              <b className={bold}>Cost-Effective Solution:</b> Hiring a professional
              landscaper can be expensive. Rescape AI offers a budget-friendly alternative
              without compromising on quality.
            </li>
            <li>
              <b className={bold}>Time-saving:</b> Traditional garden design can take
              weeks or even months. With <Link href="/garden-design-app">Rescape AI</Link>
              , you can visualize new designs in a matter of minutes.
            </li>
            <li>
              <b className={bold}>Customizable Creativity:</b> Whether you want a
              completely new look or subtle changes, the adjustable creativity settings
              give you full control over the design process.
            </li>
            <li>
              <b className={bold}>Sustainable Choices:</b> By creating a wildlife-friendly
              garden, you contribute to local biodiversity. Design features like ponds,
              native plants, and insect hotels can easily be visualized and planned with
              Rescape AI.
            </li>
          </ol>
        </section>
        <section className={articleSection}>
          <div className={largeImageGrid} style={{ marginTop: 0 }}>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>English Cottage Garden</h2>
              <img
                src="/transform-your-garden-with-rescape-ai/english-garden-cottage-garden-elements.webp"
                alt="Harmoniously curated with luscious flowers and plants that have transformed the borders from dull to delightful. Whimsical and unique."
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
            </div>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>Higher creativity, more stylized</h2>
              <img
                src="/transform-your-garden-with-rescape-ai/english-garden-cottage-garden-elements-high-creativity.webp"
                alt="A highly stylized example of creativity showcased by our garden idea generator, transforming the house and garden into a scene straight out of a fairy tale."
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
            </div>
          </div>
        </section>
        <section className={articleSection}>
          <h2
            id="how-to"
            className={`${h2Style} ${highlightHead}`}
            style={{ textAlign: "center" }}
          >
            How to Get Started with Rescape AI
          </h2>
          <ol className={orderedList}>
            <li>
              <b className={bold}>Take a High-Quality Photo:</b> Ensure your garden is
              well-lit and take a clear, focused photograph. This will serve as the base
              image for the AI to work on.
            </li>
            <li>
              <b className={bold}>Upload Your Photo:</b> Using the Rescape AI user
              interface, upload your garden photo. The platform is designed to be
              user-friendly, guiding you through each step.
            </li>
            <li>
              <b className={bold}>Set Your Preferences:</b> Adjust the creativity level
              according to your preference. Higher creativity levels allow the AI more
              freedom to innovate, while lower levels keep the design closer to the
              original image. Choose a style and set of features to add to the garden,
              optionally add in custom instructions
            </li>
            <li>
              <b className={bold}>Generate Your Designs:</b> Once your settings are in
              place, let Rescape AI do its magic. Within moments, you'll have a range of
              design options to choose from.
            </li>
            <li>
              <b className={bold}>Refine:</b> Review the generated designs, make any
              necessary tweaks, by remixing the image and download your favorite options.
              You can easily share these designs with a landscaper if you decide to
              proceed with professional implementation.
            </li>
          </ol>
        </section>
        <section className={articleSection}>
          <h2 className={h2Style}>Conclusion</h2>
          <p className={articleBodyStyle} style={{ fontSize: `${golden * 0.7}rem` }}>
            Redesigning your garden has never been easier or more exciting with the power
            of Rescape AI. By utilizing advanced AI technology, you can transform your
            outdoor space into a beautiful and functional haven without the high costs and
            time commitments associated with traditional landscaping. Whether you're
            dreaming of a tranquil wildlife pond or a stylish pergola, Rescape AI offers a
            seamless and innovative solution to all your garden design needs.
          </p>
          <p className={articleBodyStyle} style={{ fontSize: `${golden * 0.7}rem` }}>
            Embrace the future of garden design today with Rescape AI and watch your
            outdoor space come to life with stunning, personalized designs that reflect
            your unique style and preferences.
          </p>
        </section>
        <Footer />
      </div>
    </>
  )
}

export default Index
